// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { applePastDueState, isFreeTierPlan } from "@bitebalance/contracts"
import type { BillingOffersResponse, BillingSubscriptionResponse, MeResponse } from "@bitebalance/contracts"

/**
 * ADR-085 IAP-4 (web): the decisions of the signed-in Access page, kept pure so they are unit-tested apart from the markup.
 *
 * Two independent facts drive the page:
 *  - `accessMode(offers)`: does the SERVER sell on the web? `appStoreOnly` is present only while the Whop checkout is retired
 *    (`whop_checkout_retired`); absent = today's page, unchanged. The page follows this field, never the build-time `VITE_BILLING_MODE`.
 *  - `subscriptionPanel(sub, me, now)`: WHICH provider bills the account's subscription row (`provider`: absent = Whop,
 *    `apple`, `bitebalance` = the server-granted free period). The Apple panel never shows Whop's euro price or a Cancel button.
 */
export type AccessMode =
  /** `appStoreOnly` absent: today's page, byte for byte. */
  | { kind: "whop" }
  /** `appStoreOnly` present: nothing is sold here; the free period is claimed through the server; plans are bought in the iPhone app. */
  | { kind: "app_store_only"; freePeriodClaimable: boolean }

export function accessMode(offers: BillingOffersResponse): AccessMode {
  return offers.appStoreOnly
    ? { kind: "app_store_only", freePeriodClaimable: offers.appStoreOnly.freePeriodClaimable }
    : { kind: "whop" }
}

export type SubscriptionPanel =
  /** `provider` absent: a Whop row — today's panel (price from the Whop offers, Cancel button). */
  | { kind: "whop" }
  /** An Apple row: no price (the web has no StoreKit price), no Cancel (Apple owns cancellation), a Manage link. */
  | { kind: "apple"; renewsOn: string | null; ending: boolean; pastDue: "none" | "grace" | "after_grace" }
  /** The server-granted free period (`bitebalance`): "Free", an ends line, no payment rows. */
  | { kind: "grant"; endsOn: string | null; urgent: boolean }

export function subscriptionPanel(sub: BillingSubscriptionResponse, me: MeResponse, now: number): SubscriptionPanel {
  if (sub.provider === "apple") {
    return {
      kind: "apple",
      renewsOn: sub.currentPeriodEnd,
      ending: sub.cancelAtPeriodEnd || sub.state === "canceled",
      pastDue: applePastDueState(sub, me, now),
    }
  }
  if (sub.provider === "bitebalance") {
    return { kind: "grant", endsOn: sub.currentPeriodEnd, urgent: endsWithin24Hours(sub.currentPeriodEnd, now) }
  }
  return { kind: "whop" }
}

/** The same window the mobile Access screen and the reminder email use: the end is ahead and at most 24 hours away. */
function endsWithin24Hours(iso: string | null, now: number): boolean {
  if (!iso) return false
  const hours = (new Date(iso).getTime() - now) / 3_600_000
  return hours > 0 && hours <= 24
}

/**
 * The upgrade / "continue after this" block under an ACTIVE panel.
 *  - `whop_rows`: today's paid-plan rows (only while the server still sells on the web).
 *  - `iphone_app`: the truthful "choose a plan in the BiteBalance iPhone app" line (the server sells nothing here).
 *  - `null`: no block (a paid, renewing plan; an Apple row — Apple's Manage link is the way on).
 */
export function continuationHint(mode: AccessMode, sub: BillingSubscriptionResponse | null): "whop_rows" | "iphone_app" | null {
  if (!sub) return null
  const open = mode.kind === "app_store_only" ? "iphone_app" : "whop_rows"
  if (sub.provider === "apple") return null
  if (sub.provider === "bitebalance") return open
  // A Whop row: only a free-tier plan (pre-launch / free claim) or one that is already ending can be continued; a renewing paid plan cannot.
  return isFreeTierPlan(sub.planKey) || sub.cancelAtPeriodEnd || sub.state === "canceled" ? open : null
}

/** Why a server free-period grant failed, as the consumer message to show (no codes reach the screen). */
export function freeGrantFailure(err: unknown): "freeUsed" | "featureDisabled" | "failed" {
  const envelope = typeof err === "object" && err !== null ? (err as { envelope?: unknown }).envelope : null
  const code = typeof envelope === "object" && envelope !== null ? (envelope as { code?: unknown }).code : null
  if (code === "FREE_PERIOD_ALREADY_USED") return "freeUsed"
  if (code === "FEATURE_DISABLED") return "featureDisabled"
  return "failed"
}
